/**
 * Tab3_EvacuationAssets_v2.5.jsx
 * PSP/CRE Tool v2.0 - Phase 2.5 Evacuation Asset Architecture Components
 * 
 * This file contains the UI components for displaying and configuring
 * per-facility evacuation assets as defined in Phase 2 architecture.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 27, 2024
 * Version: 2.5.2 - CLEANUP-003: MUI Grid v2 prop migration (March 26, 2026)
 *   Replaced all <Grid item xs={n}> / <Grid item xs={n} md={m}> with Grid v2 syntax.
 *   No visual change.
 *
 * Version: 2.5.1 - Phase 2 Evacuation Asset Integration (UI Enhanced)
 * 
 * FEATURES:
 * - Per-facility evacuation asset display (EA-001)
 * - Organic/Attached/Detached asset tracking (EA-002)
 * - PULL/PUSH evacuation method configuration (EA-003)
 * - Evacuation chain visualization
 * - Asset utilization and capacity calculations
 * - Capacity progress bars with utilization warnings
 * - Tooltips for all capacity and asset values
 * - Attach/Detach asset buttons for Expert Mode
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Alert,
  AlertTitle,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocalHospital as MedicalIcon,
  AirplanemodeActive as HeliIcon,
  DirectionsCar as AmbulanceIcon,
  ArrowForward as ArrowIcon,
  ArrowDownward as PullIcon,
  ArrowUpward as PushIcon,
  SwapVert as AutoIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Evacuation method options
 */
const EVACUATION_METHODS = {
  PULL: {
    label: 'PULL',
    description: 'Higher echelon sends assets to pick up patients',
    icon: <PullIcon />,
    color: 'primary'
  },
  PUSH: {
    label: 'PUSH',
    description: 'Sending unit dispatches patients to higher echelon',
    icon: <PushIcon />,
    color: 'secondary'
  },
  AUTO: {
    label: 'AUTO',
    description: 'System determines best method based on conditions',
    icon: <AutoIcon />,
    color: 'default'
  }
};

/**
 * Asset type labels for display - Human readable names
 */
const ASSET_TYPE_LABELS = {
  wheeled_light: 'Light Ambulances',
  wheeled_armored: 'Armored Ambulances',
  tracked: 'Tracked Ambulances',
  allocated: 'Allocated Helicopters',
  on_call: 'On-Call Helicopters',
  medics: 'Medics',
  drivers: 'Drivers',
  litter_bearers: 'Litter Bearers',
  flight_medics: 'Flight Medics'
};

/**
 * Asset type tooltips - Explanations for each asset type
 */
const ASSET_TYPE_TOOLTIPS = {
  wheeled_light: 'Standard wheeled ambulances for road transport. Capacity: 2 litter patients. Best for permissive environments.',
  wheeled_armored: 'Armored wheeled ambulances (e.g., M113 variant). Capacity: 2 litter patients. Required for contested areas.',
  tracked: 'Fully tracked ambulances (e.g., M113A3). Capacity: 4 litter patients. Cross-country capable, required for forward areas.',
  allocated: 'MEDEVAC helicopters permanently assigned to this facility. Dedicated crew and maintenance.',
  on_call: 'MEDEVAC helicopters available on request. Shared resource, may have delayed response.',
  medics: 'Combat medics and medical technicians for patient care during evacuation.',
  drivers: 'Trained ambulance drivers/operators.',
  litter_bearers: 'Personnel for loading/unloading and carrying litter patients.',
  flight_medics: 'Specially trained medics for aeromedical evacuation.'
};

/**
 * Capacity thresholds for warnings
 */
const CAPACITY_THRESHOLDS = {
  critical: 20,    // Below 20% = critical (red)
  warning: 50,     // Below 50% = warning (orange)
  adequate: 80,    // Below 80% = adequate (yellow)
  optimal: 100     // 80%+ = optimal (green)
};

/**
 * Role level descriptions
 */
const ROLE_DESCRIPTIONS = {
  ROLE1: {
    name: 'Role 1 - Battalion Aid Station',
    evacMethod: 'Receives casualties from POI (PUSH), sends to Role 2 (PUSH)',
    holdTime: '4 hours',
    color: '#e3f2fd',
    expectedCapacity: 20 // patients per day baseline
  },
  ROLE2_BASIC: {
    name: 'Role 2 Basic - Brigade Medical Company',
    evacMethod: 'PULLS from Role 1s, PUSHes to Role 2E/3',
    holdTime: '72 hours (3 days)',
    color: '#f3e5f5',
    expectedCapacity: 60
  },
  ROLE2_ENHANCED: {
    name: 'Role 2 Enhanced - Division Medical',
    evacMethod: 'PULLS from Role 2 Basics, AUTO to Role 3',
    holdTime: '168 hours (7 days)',
    color: '#fff3e0',
    expectedCapacity: 120
  },
  ROLE3: {
    name: 'Role 3 - Corps CSH',
    evacMethod: 'PULLS from Role 2Es, PUSHes to Role 4',
    holdTime: '720 hours (30 days)',
    color: '#e8f5e9',
    expectedCapacity: 200
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate effective assets (Organic + Attached - Detached)
 */
const calculateEffectiveAssets = (facility) => {
  const organic = facility.organic_assets || {};
  const attached = facility.attached_assets || {};
  const detached = facility.detached_assets || {};
  
  const effective = {
    ground_ambulances: {
      wheeled_light: (organic.ground_ambulances?.wheeled_light || 0) +
                     (attached.ground_ambulances?.wheeled_light || 0) -
                     (detached.ground_ambulances?.wheeled_light || 0),
      wheeled_armored: (organic.ground_ambulances?.wheeled_armored || 0) +
                       (attached.ground_ambulances?.wheeled_armored || 0) -
                       (detached.ground_ambulances?.wheeled_armored || 0),
      tracked: (organic.ground_ambulances?.tracked || 0) +
               (attached.ground_ambulances?.tracked || 0) -
               (detached.ground_ambulances?.tracked || 0)
    },
    helicopters: {
      allocated: (organic.helicopters?.allocated || 0) +
                 (attached.helicopters?.allocated || 0) -
                 (detached.helicopters?.allocated || 0),
      on_call: (organic.helicopters?.on_call || 0) +
               (attached.helicopters?.on_call || 0) -
               (detached.helicopters?.on_call || 0)
    },
    personnel: {
      medics: (organic.personnel?.medics || 0) +
              (attached.personnel?.medics || 0) -
              (detached.personnel?.medics || 0),
      drivers: (organic.personnel?.drivers || 0) +
               (attached.personnel?.drivers || 0) -
               (detached.personnel?.drivers || 0),
      litter_bearers: (organic.personnel?.litter_bearers || 0) +
                      (attached.personnel?.litter_bearers || 0) -
                      (detached.personnel?.litter_bearers || 0),
      flight_medics: (organic.personnel?.flight_medics || 0) +
                     (attached.personnel?.flight_medics || 0) -
                     (detached.personnel?.flight_medics || 0)
    }
  };
  
  // Calculate totals
  effective.total_ground = effective.ground_ambulances.wheeled_light +
                          effective.ground_ambulances.wheeled_armored +
                          effective.ground_ambulances.tracked;
  effective.total_air = effective.helicopters.allocated + effective.helicopters.on_call;
  effective.total_personnel = effective.personnel.medics +
                             effective.personnel.drivers +
                             effective.personnel.litter_bearers +
                             effective.personnel.flight_medics;
  
  return effective;
};

/**
 * Calculate evacuation capacity based on assets and distance
 */
const calculateEvacuationCapacity = (facility, distanceKm = 20, environment = 'PERMISSIVE') => {
  const effective = calculateEffectiveAssets(facility);
  
  // Ground capacity: round trips per day based on distance
  const groundSpeedKmh = environment === 'CONTESTED' ? 30 : 50;
  const roundTripHours = (distanceKm * 2) / groundSpeedKmh + 0.5; // 30 min load/unload
  const groundTripsPerDay = Math.floor(12 / roundTripHours); // 12 hour ops day
  
  // Patients per trip: Light=2, Armored=2, Tracked=4
  const groundCapacity = (effective.ground_ambulances.wheeled_light * 2 +
                         effective.ground_ambulances.wheeled_armored * 2 +
                         effective.ground_ambulances.tracked * 4) * groundTripsPerDay;
  
  // Air capacity: sorties per day
  const heliSpeedKmh = 200;
  const heliRoundTripHours = (distanceKm * 2) / heliSpeedKmh + 0.25; // 15 min load/unload
  const heliSortiesPerDay = Math.floor(8 / heliRoundTripHours); // 8 hour flight ops
  const airCapacity = (effective.total_air) * 4 * heliSortiesPerDay; // 4 patients per heli
  
  return {
    ground: groundCapacity,
    air: airCapacity,
    total: groundCapacity + airCapacity,
    trips_per_day: {
      ground: groundTripsPerDay,
      air: heliSortiesPerDay
    }
  };
};

/**
 * Get capacity status color and label based on utilization
 */
const getCapacityStatus = (current, expected) => {
  if (expected === 0) return { color: 'grey', label: 'N/A', severity: 'info' };
  
  const percent = (current / expected) * 100;
  
  if (percent < CAPACITY_THRESHOLDS.critical) {
    return { color: 'error', label: 'CRITICAL', severity: 'error', percent };
  } else if (percent < CAPACITY_THRESHOLDS.warning) {
    return { color: 'warning', label: 'LOW', severity: 'warning', percent };
  } else if (percent < CAPACITY_THRESHOLDS.adequate) {
    return { color: 'info', label: 'ADEQUATE', severity: 'info', percent };
  } else {
    return { color: 'success', label: 'OPTIMAL', severity: 'success', percent };
  }
};

/**
 * Get default organic assets for a facility type
 */
const getDefaultOrganicAssets = (facilityType, unitType = 'STANDARD') => {
  const defaults = {
    ROLE1: {
      STANDARD: {
        ground_ambulances: { wheeled_light: 2, wheeled_armored: 2, tracked: 0 },
        helicopters: { allocated: 0, on_call: 0 },
        personnel: { medics: 8, drivers: 4, litter_bearers: 8, flight_medics: 0 }
      },
      LIGHT: {
        ground_ambulances: { wheeled_light: 3, wheeled_armored: 0, tracked: 0 },
        helicopters: { allocated: 0, on_call: 0 },
        personnel: { medics: 6, drivers: 3, litter_bearers: 6, flight_medics: 0 }
      },
      HEAVY: {
        ground_ambulances: { wheeled_light: 2, wheeled_armored: 2, tracked: 1 },
        helicopters: { allocated: 0, on_call: 0 },
        personnel: { medics: 10, drivers: 5, litter_bearers: 10, flight_medics: 0 }
      },
      ARTILLERY: {
        ground_ambulances: { wheeled_light: 2, wheeled_armored: 1, tracked: 0 },
        helicopters: { allocated: 0, on_call: 0 },
        personnel: { medics: 6, drivers: 3, litter_bearers: 4, flight_medics: 0 }
      }
    },
    ROLE2_BASIC: {
      STANDARD: {
        ground_ambulances: { wheeled_light: 8, wheeled_armored: 8, tracked: 0 },
        helicopters: { allocated: 0, on_call: 2 },
        personnel: { medics: 24, drivers: 16, litter_bearers: 24, flight_medics: 0 }
      },
      LIGHT_BRIGADE: {
        ground_ambulances: { wheeled_light: 10, wheeled_armored: 4, tracked: 0 },
        helicopters: { allocated: 0, on_call: 3 },
        personnel: { medics: 20, drivers: 14, litter_bearers: 20, flight_medics: 0 }
      },
      HEAVY_BRIGADE: {
        ground_ambulances: { wheeled_light: 6, wheeled_armored: 10, tracked: 2 },
        helicopters: { allocated: 0, on_call: 2 },
        personnel: { medics: 28, drivers: 18, litter_bearers: 28, flight_medics: 0 }
      }
    },
    ROLE2_ENHANCED: {
      STANDARD: {
        ground_ambulances: { wheeled_light: 15, wheeled_armored: 12, tracked: 3 },
        helicopters: { allocated: 0, on_call: 5 },
        personnel: { medics: 40, drivers: 30, litter_bearers: 36, flight_medics: 4 }
      }
    },
    ROLE3: {
      STANDARD: {
        ground_ambulances: { wheeled_light: 30, wheeled_armored: 25, tracked: 5 },
        helicopters: { allocated: 12, on_call: 8 },
        personnel: { medics: 80, drivers: 60, litter_bearers: 60, flight_medics: 24 }
      },
      ROMANIAN_CORPS: {
        ground_ambulances: { wheeled_light: 25, wheeled_armored: 25, tracked: 5 },
        helicopters: { allocated: 6, on_call: 4 },
        personnel: { medics: 60, drivers: 55, litter_bearers: 50, flight_medics: 10 }
      }
    }
  };
  
  return defaults[facilityType]?.[unitType] || defaults[facilityType]?.STANDARD || {
    ground_ambulances: { wheeled_light: 0, wheeled_armored: 0, tracked: 0 },
    helicopters: { allocated: 0, on_call: 0 },
    personnel: { medics: 0, drivers: 0, litter_bearers: 0, flight_medics: 0 }
  };
};

// Translation key mapping for asset types (tab3_evac namespace)
const ASSET_TYPE_I18N_KEYS = {
  wheeled_light:   'asset_light_ambulances',
  wheeled_armored: 'asset_armored_ambulances',
  tracked:         'asset_tracked_ambulances',
  allocated:       'asset_allocated_helicopters',
  on_call:         'asset_on_call_helicopters',
  medics:          'asset_medics',
  drivers:         'asset_drivers',
  litter_bearers:  'asset_litter_bearers',
  flight_medics:   'asset_flight_medics',
};

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * Capacity Progress Bar Component - Shows utilization with color coding
 */
export const CapacityProgressBar = ({ current, expected, label, showWarning = true }) => {
  const { t } = useTranslation();
  const status = getCapacityStatus(current, expected);
  const percent = expected > 0 ? Math.min((current / expected) * 100, 100) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {showWarning && status.severity === 'error' && (
            <Tooltip title={t('tab3_evac.progress_tooltip_critical')}>
              <WarningIcon color="error" fontSize="small" />
            </Tooltip>
          )}
          {showWarning && status.severity === 'warning' && (
            <Tooltip title={t('tab3_evac.progress_tooltip_warning')}>
              <WarningIcon color="warning" fontSize="small" />
            </Tooltip>
          )}
          <Typography variant="caption" color={`${status.color}.main`} fontWeight="bold">
            {current}/{expected} ({Math.round(percent)}%)
          </Typography>
        </Box>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={percent} 
        color={status.color}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
};

/**
 * Asset Badge Component - Shows count with icon and tooltip
 */
export const AssetBadge = ({ icon, count, label, tooltip, color = 'default' }) => (
  <Tooltip title={tooltip || `${count} ${label}`} arrow>
    <Chip
      icon={icon}
      label={`${count} ${label}`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ m: 0.5 }}
    />
  </Tooltip>
);

/**
 * Asset Row Component - Displays organic/attached/detached/effective with tooltips
 */
export const AssetRow = ({
  assetKey,
  label,
  organic,
  attached,
  detached,
  effective,
  onAttach,
  onDetach,
  readOnly = true
}) => {
  const { t } = useTranslation();
  const tooltip = t(`tab3_evac.tooltip_${assetKey}`) || ASSET_TYPE_TOOLTIPS[assetKey] || '';
  const displayLabel = ASSET_TYPE_I18N_KEYS[assetKey] ? t(`tab3_evac.${ASSET_TYPE_I18N_KEYS[assetKey]}`) : (ASSET_TYPE_LABELS[assetKey] || label);

  return (
    <TableRow>
      <TableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          {displayLabel}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="right">
              <IconButton size="small" sx={{ p: 0.25 }}>
                <InfoIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">{organic}</TableCell>
      <TableCell align="center" sx={{ color: 'success.main' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          +{attached}
          {!readOnly && onAttach && (
            <Tooltip title={t('tab3_evac.attach_tooltip', { label: displayLabel })}>
              <IconButton size="small" color="success" onClick={() => onAttach(assetKey)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ color: 'error.main' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          -{detached}
          {!readOnly && onDetach && effective > 0 && (
            <Tooltip title={t('tab3_evac.detach_tooltip', { label: displayLabel })}>
              <IconButton size="small" color="error" onClick={() => onDetach(assetKey)}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Typography fontWeight="bold" color={effective === 0 ? 'error.main' : 'text.primary'}>
            {effective}
          </Typography>
          {effective === 0 && (
            <Tooltip title={t('tab3_evac.no_assets_tooltip')}>
              <WarningIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/**
 * Evacuation Method Selector Component with tooltip
 */
export const EvacuationMethodSelector = ({
  direction,
  currentMethod,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation();
  const directionLabel = direction === 'incoming' ? t('tab3_evac.incoming_method_label') : t('tab3_evac.outgoing_method_label');
  const directionTooltip = direction === 'incoming'
    ? t('tab3_evac.incoming_method_tooltip')
    : t('tab3_evac.outgoing_method_tooltip');

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>
        <Box display="flex" alignItems="center" gap={0.5}>
          {directionLabel}
          <Tooltip title={directionTooltip} arrow>
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
      </InputLabel>
      <Select
        value={currentMethod}
        onChange={(e) => onChange(direction, e.target.value)}
        label={directionLabel}
      >
        {Object.entries(EVACUATION_METHODS).map(([key, method]) => (
          <MenuItem key={key} value={key}>
            <Tooltip title={method.description} placement="right" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {method.icon}
                <span>{method.label}</span>
              </Box>
            </Tooltip>
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        { { PULL: t('tab3_evac.pull_description'), PUSH: t('tab3_evac.push_description'), AUTO: t('tab3_evac.auto_description') }[currentMethod] }
      </FormHelperText>
    </FormControl>
  );
};

/**
 * Facility Assets Card Component
 * Displays evacuation assets for a single facility with full UI features
 */
export const FacilityAssetsCard = ({
  facility,
  facilityType,
  onMethodChange,
  onAssetAttach,
  onAssetDetach,
  expanded = false,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);
  
  const roleInfo = ROLE_DESCRIPTIONS[facilityType] || ROLE_DESCRIPTIONS.ROLE1;
  const effective = calculateEffectiveAssets(facility);
  const capacity = calculateEvacuationCapacity(facility);
  const capacityStatus = getCapacityStatus(capacity.total, roleInfo.expectedCapacity);
  
  const organic = facility.organic_assets || getDefaultOrganicAssets(facilityType);
  const attached = facility.attached_assets || { ground_ambulances: {}, helicopters: {}, personnel: {} };
  const detached = facility.detached_assets || { ground_ambulances: {}, helicopters: {}, personnel: {} };
  const evacConfig = facility.evacuation_config || { incoming_method: 'PULL', outgoing_method: 'PUSH' };
  
  // Handler for attach button
  const handleAttach = (assetCategory, assetType) => {
    if (onAssetAttach) {
      onAssetAttach(facility.id || facility.facility_id, assetCategory, assetType);
    }
  };
  
  // Handler for detach button
  const handleDetach = (assetCategory, assetType) => {
    if (onAssetDetach) {
      onAssetDetach(facility.id || facility.facility_id, assetCategory, assetType);
    }
  };
  
  return (
    <Card 
      sx={{ 
        mb: 2, 
        border: '1px solid',
        borderColor: capacityStatus.severity === 'error' ? 'error.main' : 'divider',
        bgcolor: roleInfo.color
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {facility.facility_name || facility.name || 'Medical Facility'}
            </Typography>
            <Tooltip title={roleInfo.evacMethod} arrow>
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                {roleInfo.name} | Hold: {roleInfo.holdTime}
              </Typography>
            </Tooltip>
          </Box>
          <Box display="flex" gap={0.5} alignItems="center">
            {capacityStatus.severity === 'error' && (
              <Tooltip title={t('tab3_evac.capacity_critical_tooltip')}>
                <WarningIcon color="error" />
              </Tooltip>
            )}
            {capacityStatus.severity === 'warning' && (
              <Tooltip title={t('tab3_evac.capacity_warning_tooltip')}>
                <WarningIcon color="warning" />
              </Tooltip>
            )}
            <AssetBadge
              icon={<AmbulanceIcon fontSize="small" />}
              count={effective.total_ground}
              label={t('tab3_evac.facility_ground_evac')}
              tooltip={`${effective.total_ground} ground ambulances available for evacuation`}
              color="primary"
            />
            <AssetBadge
              icon={<HeliIcon fontSize="small" />}
              count={effective.total_air}
              label={t('tab3_evac.facility_air_evac')}
              tooltip={`${effective.total_air} helicopters (${effective.helicopters.allocated} allocated, ${effective.helicopters.on_call} on-call)`}
              color="secondary"
            />
          </Box>
        </Box>
        
        {/* Capacity Progress Bars */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid size={12}>
            <Tooltip 
              title={`Total evacuation capacity: ${capacity.total} patients/day. Expected: ${roleInfo.expectedCapacity} patients/day.`}
              arrow
            >
              <Box>
                <CapacityProgressBar 
                  current={capacity.total}
                  expected={roleInfo.expectedCapacity}
                  label="Total Evacuation Capacity"
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid size={6}>
            <Tooltip 
              title={`Ground: ${capacity.ground} patients/day (${capacity.trips_per_day.ground} trips/vehicle/day)`}
              arrow
            >
              <Box>
                <CapacityProgressBar 
                  current={capacity.ground}
                  expected={Math.round(roleInfo.expectedCapacity * 0.7)}
                  label="Ground Evac"
                  showWarning={false}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid size={6}>
            <Tooltip 
              title={`Air: ${capacity.air} patients/day (${capacity.trips_per_day.air} sorties/heli/day)`}
              arrow
            >
              <Box>
                <CapacityProgressBar 
                  current={capacity.air}
                  expected={Math.round(roleInfo.expectedCapacity * 0.3)}
                  label="Air Evac"
                  showWarning={false}
                />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
        
        {/* Evacuation Method Chips */}
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          <Tooltip title={EVACUATION_METHODS[evacConfig.incoming_method]?.description} arrow>
            <Chip
              icon={EVACUATION_METHODS[evacConfig.incoming_method]?.icon}
              label={t('tab3_evac.chip_in_method', { method: evacConfig.incoming_method })}
              size="small"
              variant="outlined"
            />
          </Tooltip>
          <Tooltip title={EVACUATION_METHODS[evacConfig.outgoing_method]?.description} arrow>
            <Chip
              icon={EVACUATION_METHODS[evacConfig.outgoing_method]?.icon}
              label={t('tab3_evac.chip_out_method', { method: evacConfig.outgoing_method })}
              size="small"
              variant="outlined"
            />
          </Tooltip>
          {evacConfig.picks_up_from?.length > 0 && (
            <Tooltip title={t('tab3_evac.chip_pulls_from_tooltip', { count: evacConfig.picks_up_from.length })} arrow>
              <Chip
                label={t('tab3_evac.chip_pulls_from', { count: evacConfig.picks_up_from.length })}
                size="small"
                color="info"
              />
            </Tooltip>
          )}
        </Box>
      </CardContent>
      
      {/* Expandable Details */}
      <Accordion 
        expanded={isExpanded} 
        onChange={() => setIsExpanded(!isExpanded)}
        sx={{ bgcolor: 'transparent', boxShadow: 'none' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">
            {t('tab3_evac.accordion_header_details')}
            {!readOnly && (
              <Chip label={t('tab3_evac.accordion_chip_expert_mode')} size="small" color="warning" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Asset Breakdown Table - Ground Ambulances */}
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AmbulanceIcon fontSize="small" color="primary" />
            {t('tab3_evac.ground_ambulances_section')}
            <Tooltip title={t('tab3_evac.ground_ambulances_tooltip')} arrow>
              <IconButton size="small">
                <InfoIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>{t('tab3_evac.table_header_type')}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('tab3_evac.table_header_organic_tooltip')} arrow>
                      <span>{t('tab3_evac.table_header_organic')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('tab3_evac.table_header_attached_tooltip')} arrow>
                      <span>{t('tab3_evac.table_header_attached')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('tab3_evac.table_header_detached_tooltip')} arrow>
                      <span>{t('tab3_evac.table_header_detached')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('tab3_evac.table_header_effective_tooltip')} arrow>
                      <strong>{t('tab3_evac.table_header_effective')}</strong>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AssetRow
                  assetKey="wheeled_light"
                  label={t('tab3_evac.asset_light_wheeled')}
                  organic={organic.ground_ambulances?.wheeled_light || 0}
                  attached={attached.ground_ambulances?.wheeled_light || 0}
                  detached={detached.ground_ambulances?.wheeled_light || 0}
                  effective={effective.ground_ambulances.wheeled_light}
                  readOnly={readOnly}
                  onAttach={(key) => handleAttach('ground_ambulances', key)}
                  onDetach={(key) => handleDetach('ground_ambulances', key)}
                />
                <AssetRow
                  assetKey="wheeled_armored"
                  label={t('tab3_evac.asset_armored_wheeled')}
                  organic={organic.ground_ambulances?.wheeled_armored || 0}
                  attached={attached.ground_ambulances?.wheeled_armored || 0}
                  detached={detached.ground_ambulances?.wheeled_armored || 0}
                  effective={effective.ground_ambulances.wheeled_armored}
                  readOnly={readOnly}
                  onAttach={(key) => handleAttach('ground_ambulances', key)}
                  onDetach={(key) => handleDetach('ground_ambulances', key)}
                />
                <AssetRow 
                  assetKey="tracked"
                  label="Tracked"
                  organic={organic.ground_ambulances?.tracked || 0}
                  attached={attached.ground_ambulances?.tracked || 0}
                  detached={detached.ground_ambulances?.tracked || 0}
                  effective={effective.ground_ambulances.tracked}
                  readOnly={readOnly}
                  onAttach={(key) => handleAttach('ground_ambulances', key)}
                  onDetach={(key) => handleDetach('ground_ambulances', key)}
                />
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>{t('tab3_evac.asset_total_ground')}</strong></TableCell>
                  <TableCell align="center" colSpan={3}></TableCell>
                  <TableCell align="center">
                    <strong>{effective.total_ground}</strong>
                    {effective.total_ground === 0 && (
                      <Tooltip title={t('tab3_evac.no_ground_ambulances')}>
                        <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Helicopters Table */}
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeliIcon fontSize="small" color="secondary" />
            {t('tab3_evac.helicopters_section')}
            <Tooltip title={t('tab3_evac.helicopters_tooltip')} arrow>
              <IconButton size="small">
                <InfoIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Organic</TableCell>
                  <TableCell align="center">Attached</TableCell>
                  <TableCell align="center">Detached</TableCell>
                  <TableCell align="center"><strong>Effective</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AssetRow
                  assetKey="allocated"
                  label={t('tab3_evac.asset_allocated')}
                  organic={organic.helicopters?.allocated || 0}
                  attached={attached.helicopters?.allocated || 0}
                  detached={detached.helicopters?.allocated || 0}
                  effective={effective.helicopters.allocated}
                  readOnly={readOnly}
                  onAttach={(key) => handleAttach('helicopters', key)}
                  onDetach={(key) => handleDetach('helicopters', key)}
                />
                <AssetRow
                  assetKey="on_call"
                  label={t('tab3_evac.asset_on_call')}
                  organic={organic.helicopters?.on_call || 0}
                  attached={attached.helicopters?.on_call || 0}
                  detached={detached.helicopters?.on_call || 0}
                  effective={effective.helicopters.on_call}
                  readOnly={readOnly}
                  onAttach={(key) => handleAttach('helicopters', key)}
                  onDetach={(key) => handleDetach('helicopters', key)}
                />
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>{t('tab3_evac.asset_total_air')}</strong></TableCell>
                  <TableCell align="center" colSpan={3}></TableCell>
                  <TableCell align="center">
                    <strong>{effective.total_air}</strong>
                    {effective.total_air === 0 && facilityType !== 'ROLE1' && (
                      <Tooltip title={t('tab3_evac.no_helicopters')}>
                        <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Evacuation Method Configuration */}
          {!readOnly && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('tab3_evac.config_section_title')}
                <Tooltip title={t('tab3_evac.config_section_tooltip')} arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <EvacuationMethodSelector
                    direction="incoming"
                    currentMethod={evacConfig.incoming_method}
                    onChange={onMethodChange}
                    disabled={facilityType === 'ROLE1'} // Role 1 always receives PUSH
                  />
                </Grid>
                <Grid size={6}>
                  <EvacuationMethodSelector
                    direction="outgoing"
                    currentMethod={evacConfig.outgoing_method}
                    onChange={onMethodChange}
                    disabled={facilityType === 'ROLE3'} // Role 3 always sends PUSH to Role 4
                  />
                </Grid>
              </Grid>
              
              {/* Action Buttons */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAttach('ground_ambulances', 'wheeled_light')}
                >
                  {t('tab3_evac.button_request_assets')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<RemoveIcon />}
                  disabled={effective.total_ground + effective.total_air === 0}
                  onClick={() => handleDetach('ground_ambulances', 'wheeled_light')}
                >
                  {t('tab3_evac.button_detach_assets')}
                </Button>
              </Box>
            </>
          )}
          
          {/* Supported Facilities */}
          {evacConfig.picks_up_from?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('tab3_evac.pulls_from_section')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {evacConfig.picks_up_from.map((id, idx) => (
                  <Tooltip key={idx} title={`Collects casualties from facility ${id}`} arrow>
                    <Chip 
                      label={id}
                      size="small"
                      variant="outlined"
                      icon={<PullIcon fontSize="small" />}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}
          
          {evacConfig.delivers_to && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('tab3_evac.delivers_to_section')}
              </Typography>
              <Tooltip title={`Sends casualties to ${evacConfig.delivers_to}`} arrow>
                <Chip 
                  label={evacConfig.delivers_to}
                  size="small"
                  color="secondary"
                  icon={<PushIcon fontSize="small" />}
                />
              </Tooltip>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

/**
 * Evacuation Chain Visualization Component
 * Shows the full evacuation chain from Role 1 to Role 4 with tooltips
 */
export const EvacuationChainVisualization = ({
  role1Count,
  role2Count,
  role2eCount,
  role3Count,
  chainConfigured = false
}) => {
  const { t } = useTranslation();
  return (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        🔗 Evacuation Chain
        {chainConfigured ? (
          <Tooltip title={t('tab3_evac.chain_configured_tooltip')} arrow>
            <CheckIcon color="success" fontSize="small" />
          </Tooltip>
        ) : (
          <Tooltip title={t('tab3_evac.chain_incomplete_tooltip')} arrow>
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        )}
        <Tooltip
          title={t('tab3_evac.chain_info_tooltip')}
          arrow
        >
          <IconButton size="small">
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-around',
        py: 2,
        px: 1,
        bgcolor: 'grey.50',
        borderRadius: 1,
        flexWrap: 'wrap',
        gap: 1
      }}>
        {/* POI */}
        <Tooltip title={t('tab3_evac.poi_tooltip')} arrow>
          <Box textAlign="center">
            <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#ffebee', minWidth: 80 }}>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.poi_label')}</Typography>
              <Typography variant="caption">{t('tab3_evac.poi_description')}</Typography>
            </Paper>
          </Box>
        </Tooltip>

        <ArrowIcon color="action" />
        <Tooltip title={t('tab3_evac.push_to_role1_tooltip')} arrow>
          <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>{t('tab3_evac.push_label')}</Typography>
        </Tooltip>
        
        {/* Role 1 */}
        <Tooltip title={t('tab3_evac.role1_tooltip', { count: role1Count })} arrow>
          <Box textAlign="center">
            <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#e3f2fd', minWidth: 80 }}>
              <Typography variant="h6" color="primary">{role1Count}</Typography>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.role1_box_title')}</Typography>
              <Typography variant="caption">{t('tab3_evac.role1_box_subtext')}</Typography>
            </Paper>
          </Box>
        </Tooltip>

        <Box textAlign="center">
          <PullIcon color="primary" sx={{ transform: 'rotate(90deg)' }} />
          <Tooltip title={t('tab3_evac.pull_from_role1_tooltip')} arrow>
            <Typography variant="caption" display="block" sx={{ color: 'primary.main', cursor: 'help' }}>{t('tab3_evac.pull_label')}</Typography>
          </Tooltip>
        </Box>
        
        {/* Role 2B */}
        <Tooltip title={t('tab3_evac.role2b_tooltip', { count: role2Count })} arrow>
          <Box textAlign="center">
            <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#f3e5f5', minWidth: 80 }}>
              <Typography variant="h6" color="secondary">{role2Count}</Typography>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.role2b_title')}</Typography>
              <Typography variant="caption">{t('tab3_evac.role2b_subtext')}</Typography>
            </Paper>
          </Box>
        </Tooltip>

        <Box textAlign="center">
          <PullIcon color="secondary" sx={{ transform: 'rotate(90deg)' }} />
          <Tooltip title={t('tab3_evac.pull_from_role2_tooltip')} arrow>
            <Typography variant="caption" display="block" sx={{ color: 'secondary.main', cursor: 'help' }}>{t('tab3_evac.pull_label')}</Typography>
          </Tooltip>
        </Box>
        
        {/* Role 2E */}
        <Tooltip title={t('tab3_evac.role2e_tooltip', { count: role2eCount })} arrow>
          <Box textAlign="center">
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                bgcolor: '#fff3e0',
                minWidth: 80,
                border: role2eCount === 0 ? '2px dashed' : 'none',
                borderColor: 'warning.main'
              }}
            >
              <Typography variant="h6" sx={{ color: role2eCount === 0 ? 'warning.main' : 'warning.dark' }}>
                {role2eCount}
                {role2eCount === 0 && (
                  <Tooltip title={t('tab3_evac.no_role2e_tooltip')}>
                    <WarningIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.role2e_title')}</Typography>
              <Typography variant="caption">{t('tab3_evac.role2e_subtext')}</Typography>
            </Paper>
          </Box>
        </Tooltip>

        <Box textAlign="center">
          <PullIcon sx={{ transform: 'rotate(90deg)', color: 'warning.main' }} />
          <Tooltip title={t('tab3_evac.pull_to_role3_tooltip')} arrow>
            <Typography variant="caption" display="block" sx={{ color: 'warning.main', cursor: 'help' }}>{t('tab3_evac.pull_label')}</Typography>
          </Tooltip>
        </Box>
        
        {/* Role 3 */}
        <Tooltip title={t('tab3_evac.role3_tooltip', { count: role3Count })} arrow>
          <Box textAlign="center">
            <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#e8f5e9', minWidth: 80 }}>
              <Typography variant="h6" color="success">{role3Count}</Typography>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.role3_title')}</Typography>
              <Typography variant="caption">{t('tab3_evac.role3_subtext')}</Typography>
            </Paper>
          </Box>
        </Tooltip>

        <ArrowIcon color="action" />
        <Tooltip title={t('tab3_evac.stratevac_tooltip')} arrow>
          <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'help' }}>{t('tab3_evac.stratevac_label')}</Typography>
        </Tooltip>

        {/* Role 4 */}
        <Tooltip title={t('tab3_evac.role4_tooltip')} arrow>
          <Box textAlign="center">
            <Paper elevation={2} sx={{ p: 1.5, bgcolor: '#fce4ec', minWidth: 80 }}>
              <Typography variant="body2" fontWeight="bold">{t('tab3_evac.role4_title')}</Typography>
              <Typography variant="caption">{t('tab3_evac.role4_subtext')}</Typography>
            </Paper>
          </Box>
        </Tooltip>
      </Box>
      
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          {t('tab3_evac.alert_evacuation_chain')}
        </Typography>
      </Alert>
    </CardContent>
  </Card>
  );
};

/**
 * Asset Summary Card Component
 * Shows aggregate asset totals across all facilities with progress bars
 */
export const AssetSummaryCard = ({ facilities, expectedCasualties = 100 }) => {
  const { t } = useTranslation();
  const totals = {
    ground: { light: 0, armored: 0, tracked: 0 },
    air: { allocated: 0, on_call: 0 },
    capacity: { ground: 0, air: 0 }
  };
  
  facilities.forEach(f => {
    const effective = calculateEffectiveAssets(f);
    const capacity = calculateEvacuationCapacity(f);
    
    totals.ground.light += effective.ground_ambulances.wheeled_light;
    totals.ground.armored += effective.ground_ambulances.wheeled_armored;
    totals.ground.tracked += effective.ground_ambulances.tracked;
    totals.air.allocated += effective.helicopters.allocated;
    totals.air.on_call += effective.helicopters.on_call;
    totals.capacity.ground += capacity.ground;
    totals.capacity.air += capacity.air;
  });
  
  const totalGround = totals.ground.light + totals.ground.armored + totals.ground.tracked;
  const totalAir = totals.air.allocated + totals.air.on_call;
  const totalCapacity = totals.capacity.ground + totals.capacity.air;
  const capacityStatus = getCapacityStatus(totalCapacity, expectedCasualties);
  
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📊 {t('tab3_evac.asset_summary_title')}
          {capacityStatus.severity === 'error' && (
            <Tooltip title={t('tab3_evac.capacity_critical_tooltip')}>
              <WarningIcon color="error" />
            </Tooltip>
          )}
          {capacityStatus.severity === 'warning' && (
            <Tooltip title={t('tab3_evac.capacity_warning_tooltip')}>
              <WarningIcon color="warning" />
            </Tooltip>
          )}
        </Typography>
        
        {/* Overall Capacity Progress */}
        <Box sx={{ mb: 2 }}>
          <CapacityProgressBar
            current={totalCapacity}
            expected={expectedCasualties}
            label={t('tab3_evac.total_evacuation_capacity')}
          />
        </Box>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AmbulanceIcon /> {t('tab3_evac.ground_assets_title')}
                <Tooltip title={t('tab3_evac.ground_assets_tooltip')} arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Tooltip title={ASSET_TYPE_TOOLTIPS.wheeled_light} arrow>
                    <Box textAlign="center" sx={{ cursor: 'help' }}>
                      <Typography variant="h4" color="primary">{totals.ground.light}</Typography>
                      <Typography variant="caption">{ASSET_TYPE_LABELS.wheeled_light.split(' ')[0]}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={4}>
                  <Tooltip title={ASSET_TYPE_TOOLTIPS.wheeled_armored} arrow>
                    <Box textAlign="center" sx={{ cursor: 'help' }}>
                      <Typography variant="h4" color="primary">{totals.ground.armored}</Typography>
                      <Typography variant="caption">{ASSET_TYPE_LABELS.wheeled_armored.split(' ')[0]}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={4}>
                  <Tooltip title={ASSET_TYPE_TOOLTIPS.tracked} arrow>
                    <Box textAlign="center" sx={{ cursor: 'help' }}>
                      <Typography variant="h4" color="primary">{totals.ground.tracked}</Typography>
                      <Typography variant="caption">{ASSET_TYPE_LABELS.tracked}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <CapacityProgressBar
                  current={totals.capacity.ground}
                  expected={Math.round(expectedCasualties * 0.7)}
                  label={t('tab3_evac.capacity_ground_label')}
                  showWarning={false}
                />
              </Box>
              <Typography variant="body2">
                {t('tab3_evac.total_ground_summary', { totalGround, totalCapacity: totals.capacity.ground })}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'secondary.50' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HeliIcon /> {t('tab3_evac.air_assets_title')}
                <Tooltip title={t('tab3_evac.air_assets_tooltip')} arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Tooltip title={ASSET_TYPE_TOOLTIPS.allocated} arrow>
                    <Box textAlign="center" sx={{ cursor: 'help' }}>
                      <Typography variant="h4" color="secondary">{totals.air.allocated}</Typography>
                      <Typography variant="caption">{ASSET_TYPE_LABELS.allocated.split(' ')[0]}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid size={6}>
                  <Tooltip title={ASSET_TYPE_TOOLTIPS.on_call} arrow>
                    <Box textAlign="center" sx={{ cursor: 'help' }}>
                      <Typography variant="h4" color="secondary">{totals.air.on_call}</Typography>
                      <Typography variant="caption">{ASSET_TYPE_LABELS.on_call.split(' ')[0]}</Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mb: 1 }}>
                <CapacityProgressBar
                  current={totals.capacity.air}
                  expected={Math.round(expectedCasualties * 0.3)}
                  label={t('tab3_evac.capacity_air_label')}
                  showWarning={false}
                />
              </Box>
              <Typography variant="body2">
                {t('tab3_evac.total_air_summary', { totalAir, totalCapacity: totals.capacity.air })}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={12}>
            <Alert severity={capacityStatus.severity}>
              <Typography variant="body1">
                {t('tab3_evac.combined_capacity_alert', { totalCapacity })}
                {capacityStatus.severity === 'error' && (
                  <span> — {t('tab3_evac.combined_capacity_error')}</span>
                )}
                {capacityStatus.severity === 'warning' && (
                  <span> — {t('tab3_evac.combined_capacity_warning')}</span>
                )}
                {capacityStatus.severity === 'success' && (
                  <span> — {t('tab3_evac.combined_capacity_success')}</span>
                )}
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

/**
 * Main Evacuation Assets Section Component
 * To be integrated into Tab3_Medical.jsx
 */
export const EvacuationAssetsSection = ({
  facilities,
  evacuationChain,
  onFacilityUpdate,
  onAssetAttach,
  onAssetDetach,
  expectedCasualties = 100,
  mode = 'STANDARD'
}) => {
  const { t } = useTranslation();
  // Group facilities by type
  const role1s = facilities.filter(f => f.type === 'ROLE1');
  const role2s = facilities.filter(f => f.type === 'ROLE2_BASIC');
  const role2es = facilities.filter(f => f.type === 'ROLE2_ENHANCED');
  const role3s = facilities.filter(f => f.type === 'ROLE3');
  
  const handleMethodChange = (facilityId, direction, method) => {
    if (onFacilityUpdate) {
      onFacilityUpdate(facilityId, {
        evacuation_config: {
          [`${direction}_method`]: method
        }
      });
    }
  };
  
  return (
    <Accordion defaultExpanded sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🚑 {t('tab3_evac.evac_assets_accordion_title')}
          {mode === 'EXPERT' && (
            <Chip label={t('tab3_evac.accordion_chip_expert_mode')} size="small" color="warning" />
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Chain Visualization */}
        <EvacuationChainVisualization
          role1Count={role1s.length}
          role2Count={role2s.length}
          role2eCount={role2es.length}
          role3Count={role3s.length}
          chainConfigured={evacuationChain?.configured || false}
        />
        
        {/* Asset Summary */}
        <AssetSummaryCard facilities={facilities} expectedCasualties={expectedCasualties} />
        
        {/* Role 3 Facilities */}
        {role3s.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicalIcon color="success" /> {t('tab3_evac.role3_section_title')}
              <Tooltip title={t('tab3_evac.role3_section_tooltip')} arrow>
                <IconButton size="small">
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Typography>
            {role3s.map(facility => (
              <FacilityAssetsCard
                key={facility.id || facility.facility_id}
                facility={facility}
                facilityType="ROLE3"
                onMethodChange={(dir, method) => handleMethodChange(facility.id || facility.facility_id, dir, method)}
                onAssetAttach={onAssetAttach}
                onAssetDetach={onAssetDetach}
                readOnly={mode === 'STANDARD'}
              />
            ))}
          </Box>
        )}
        
        {/* Role 2E Facilities */}
        {role2es.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicalIcon sx={{ color: 'warning.main' }} /> {t('tab3_evac.role2e_section_title')}
              <Tooltip title={t('tab3_evac.role2e_section_tooltip')} arrow>
                <IconButton size="small">
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Typography>
            {role2es.map(facility => (
              <FacilityAssetsCard
                key={facility.id || facility.facility_id}
                facility={facility}
                facilityType="ROLE2_ENHANCED"
                onMethodChange={(dir, method) => handleMethodChange(facility.id || facility.facility_id, dir, method)}
                onAssetAttach={onAssetAttach}
                onAssetDetach={onAssetDetach}
                readOnly={mode === 'STANDARD'}
              />
            ))}
          </Box>
        )}
        
        {/* Role 2B Facilities */}
        {role2s.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicalIcon color="secondary" /> {t('tab3_evac.role2b_section_title')}
              <Tooltip title={t('tab3_evac.role2b_section_tooltip')} arrow>
                <IconButton size="small">
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Typography>
            {role2s.map(facility => (
              <FacilityAssetsCard
                key={facility.id || facility.facility_id}
                facility={facility}
                facilityType="ROLE2_BASIC"
                onMethodChange={(dir, method) => handleMethodChange(facility.id || facility.facility_id, dir, method)}
                onAssetAttach={onAssetAttach}
                onAssetDetach={onAssetDetach}
                readOnly={mode === 'STANDARD'}
              />
            ))}
          </Box>
        )}
        
        {/* Role 1 Facilities - Collapsed by default due to volume */}
        {role1s.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalIcon color="primary" /> {t('tab3_evac.role1_accordion_title', { count: role1s.length })}
                <Tooltip title={t('tab3_evac.role1_section_tooltip')} arrow>
                  <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('tab3_evac.role1_alert_text')}
              </Alert>
              {role1s.map(facility => (
                <FacilityAssetsCard
                  key={facility.id || facility.facility_id}
                  facility={facility}
                  facilityType="ROLE1"
                  onMethodChange={(dir, method) => handleMethodChange(facility.id || facility.facility_id, dir, method)}
                  onAssetAttach={onAssetAttach}
                  onAssetDetach={onAssetDetach}
                  readOnly={true} // Role 1 methods are fixed
                />
              ))}
            </AccordionDetails>
          </Accordion>
        )}
        
        {facilities.length === 0 && (
          <Alert severity="warning">
            <AlertTitle>{t('tab3_evac.no_facilities_alert_title')}</AlertTitle>
            {t('tab3_evac.no_facilities_alert_text')}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export default EvacuationAssetsSection;

export {
  calculateEffectiveAssets,
  calculateEvacuationCapacity,
  getDefaultOrganicAssets,
  getCapacityStatus,
  EVACUATION_METHODS,
  ROLE_DESCRIPTIONS,
  ASSET_TYPE_LABELS,
  ASSET_TYPE_TOOLTIPS,
  CAPACITY_THRESHOLDS
};
